import React, { useState, useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { v4 as uuidv4 } from "uuid"
import { Link, useLocation } from "react-router-dom"
import ReactPaginate from "react-paginate"
import Rating from "./ui/Rating"
import Socials from "./ui/Socials"
import { getAverageRating } from "../util"
import AddToCartButton from "./AddToCartButton"
import WishListButton from "./ui/WishListButton"
import { useParallax } from "react-scroll-parallax"
import { fetchProducts, getWishlist } from "../store"
import {
  SearchIcon,
  ArrowBigLeftDashIcon,
  ArrowBigRightDashIcon,
} from "lucide-react"
import "./styles/Products.css"

const PaginatedProducts = () => {
  const { products, wishlist } = useSelector((state) => state.products)
  const { id } = useSelector((state) => state.auth)
  const dispatch = useDispatch()

  const [selectedCategory, setSelectedCategory] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [isOnProductsPage, setIsOnProductsPage] = useState(false)

  // Pagination variables
  const [itemsPerPage, setItemsPerPage] = useState(24)
  const [itemOffset, setItemOffset] = useState(0)

  const endOffset = itemOffset + itemsPerPage

  const { ref } = useParallax({ speed: 50 })

  const location = useLocation()

  useEffect(() => {
    if (id) {
      dispatch(getWishlist(id))
    }
  }, [id, dispatch])

  if (!products) {
    return <>Loading...</>
  }

  // Hook to listen for changes in the url to determine if we are on the products page
  useEffect(() => {
    setIsOnProductsPage(location.pathname === "/products")
  }, [location])

  // Filter products based on search query and selected category
  // useMemo is used to prevent the filterProducts array from being recalculated on every render
  const filterProducts = useMemo(() => {
    if (!isOnProductsPage) {
      return products
    }
    return products.filter(
      (product) =>
        (selectedCategory === "" || product.category === selectedCategory) &&
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [products, selectedCategory, searchQuery, isOnProductsPage])

  // Set the item offset when the search query or selected category changes
  useEffect(() => {
    setItemOffset(0)
  }, [searchQuery, selectedCategory])

  const currentProducts = filterProducts.slice(itemOffset, endOffset)
  const pageCount = Math.ceil(filterProducts.length / itemsPerPage)

  const handlePageClick = (event) => {
    const newOffset = event.selected * itemsPerPage
    setItemOffset(newOffset)
    window.scrollTo(0, 0)
  }

  return (
    <div ref={ref}>
      <div className="products-title">Products</div>
      <div className="products-container">
        {/* Filter by category */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="selectectCat"
        >
          <option value="">All Categories</option>
          <option value="Category1">Category 1</option>
          <option value="Category2">Category 2</option>
          {/* Add more options for your categories */}
        </select>

        {/* Search input */}
        <div className="join">
          <div className="btn btn-square join-item" disabled>
            <SearchIcon size={24} className="search-icon" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
            className="join"
          ></input>
        </div>
      </div>

      {/* Display filtered products */}
      <Products currentProducts={currentProducts} wishlist={wishlist} />

      {/* Pagination */}
      <ReactPaginate
        breakLabel="..."
        nextLabel={<ArrowBigRightDashIcon size={24} />}
        onPageChange={handlePageClick}
        pageRangeDisplayed={5}
        pageCount={pageCount}
        previousLabel={<ArrowBigLeftDashIcon size={24} />}
        renderOnZeroPageCount={null}
        className="assigned-name"
        activeClassName="active-name"
        pageClassName="page-class"
        previousClassName="previous-class"
        nextClassName="next-class"
      />
    </div>
  )
}

export const Products = ({ currentProducts, wishlist }) => {
  return (
    <div className="card-container">
      {currentProducts.map((product) => {
        return (
          <div className="card" key={uuidv4()}>
            <figure className="figure-container">
              <img
                src={product.imageURL}
                alt={product.name}
                className="figure-img"
              />
            </figure>
            <div className="card-body">
              <Link to={`/products/${product.id}`}>
                <h2 className="card-text">{product.name}</h2>
              </Link>
            </div>
            <div className="card-actions">
              <div className="avgrating">
                <div className="actual-rating">
                  <Rating rating={getAverageRating(product.reviews)} />
                </div>
                <div className="grow"></div>
                <div className="actual-rating">
                  <span className="badge badge-ghost">
                    <span className="badger">$</span>
                    <span className="prod-price">{product.price}</span>
                  </span>
                </div>
              </div>
              <div className="card-options">
                <WishListButton product={product} wishlist={wishlist} />
                <AddToCartButton product={product} quantity={1} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default PaginatedProducts
